import { T } from '@transifex/react';
import { cn, Text, HStack, VStack } from '@vidahealth/vida-ui';
import { ComponentProps } from 'react';
import { Text as RNText } from 'react-native';

import { CtaButton } from '@/modules/ui';

type FooterProps = {
  space?: ComponentProps<typeof VStack>['space'];
  className?: string;
  isSubmitting?: boolean;
  // Disclaimer paragraphs server-attached to legal documents (e.g. the
  // cookies/marketing copy on the Privacy Policy doc). Sourced from
  // useLegalDocuments.additionalLanguages — not translated client-side
  // because the server already returns locale-appropriate copy.
  additionalLanguages?: string[];
  onContinue: () => void;
} &
  // `onSignIn` is only wired to the sign-in link, so it's required exactly when
  // `showSignIn` is true and disallowed otherwise — no dummy handler needed for
  // footers that hide the link (e.g. the SSO flow).
  ({ showSignIn: true; onSignIn: () => void } | { showSignIn?: false; onSignIn?: never });

export const Footer = ({
  space = '4xl',
  className,
  showSignIn,
  isSubmitting = false,
  additionalLanguages = [],
  onContinue,
  onSignIn,
}: FooterProps) => {
  return (
    <VStack className={cn('mx-auto w-full pb-4', className)} space={space}>
      <CtaButton
        isDisabled={isSubmitting}
        isBusy={isSubmitting}
        onPress={onContinue}
        testID='account-creation-continue'
      >
        <T _str='Continue' />
      </CtaButton>
      {showSignIn ? (
        <HStack className='items-center justify-center gap-1'>
          <Text size='md'>
            <T _str='Already have an account?' />
          </Text>
          <RNText
            onPress={onSignIn}
            className='text-base text-primary-500'
            role='link'
            testID='account-creation-sign-in'
          >
            <T _str='Sign in' />
          </RNText>
        </HStack>
      ) : null}
      {additionalLanguages.map((text) => (
        <Text key={text} size='xs' className='px-2 leading-relaxed text-typography-500'>
          {text}
        </Text>
      ))}
    </VStack>
  );
};
